/**
 * =============================================================================
 * Hook: useReferralPayoutAttachments
 * Versão: 1.0.0 (2026-04-29)
 *
 * Descrição:
 *   Hook responsável por listar, fazer upload e remover comprovantes de
 *   pagamento (anexos) vinculados a um `affiliate_payouts`.
 *
 *   - Listagem via RPC `list_payout_attachments` (autoriza tanto admin do
 *     tenant quanto o afiliado dono).
 *   - Upload no bucket público `affiliate`, prefixo:
 *       affiliate/financeiro/{tenant_id}/{payout_id}/{uuid}-{filename}
 *   - Persistência do registro via RPC `add_payout_attachment` (admin only).
 *   - Remoção via RPC `delete_payout_attachment` (admin only) seguida de
 *     `storage.remove`.
 *
 * Segurança / validações:
 *   - Limite de 10 MB por arquivo (validado também na RPC).
 *   - Tipos permitidos: imagens (png/jpg/webp/gif) e PDF.
 *   - Sanitização de filename para evitar path traversal.
 * =============================================================================
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import { sanitizeStorageFileName } from '@/lib/storageFileName';

const log = createLogger('useReferralPayoutAttachments');

const BUCKET = 'affiliate';
const MAX_BYTES = 10 * 1024 * 1024; // 10 MB
const ALLOWED_MIME = new Set([
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/webp',
  'image/gif',
  'application/pdf',
]);

/** Item devolvido pela RPC list_payout_attachments. */
export interface PayoutAttachment {
  id: string;
  payout_id: string;
  affiliate_id: string;
  storage_path: string;
  file_name: string;
  file_size: number | null;
  mime_type: string | null;
  uploaded_by: string | null;
  created_at: string;
}

/**
 * Converte MIME em tipo genérico usado pelo AttachmentThumbnail.
 */
export function mimeToFileType(
  mime: string | null,
): 'image' | 'pdf' | 'document' | 'spreadsheet' | 'other' {
  if (!mime) return 'other';
  if (mime.startsWith('image/')) return 'image';
  if (mime === 'application/pdf') return 'pdf';
  return 'other';
}

/**
 * Sanitiza um filename: remove path separators e caracteres perigosos.
 * Mantém extensão e caracteres acentuados.
 */
// v1.x: delegado ao utilitário central (remove emojis/acentos e evita
// o erro "Invalid key" do Supabase Storage). O nome original permanece no banco.
function sanitizeFileName(fileName: string): string {
  return sanitizeStorageFileName(fileName);
}

/**
 * Devolve a URL pública do arquivo (bucket é public=true).
 */
export function getPayoutAttachmentPublicUrl(storagePath: string): string {
  const { data } = supabase.storage.from(BUCKET).getPublicUrl(storagePath);
  return data.publicUrl;
}

export function useReferralPayoutAttachments(payoutId: string | null) {
  const qc = useQueryClient();
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id ?? null;

  // ---------------------------------------------------------------------------
  // QUERY: lista de anexos
  // ---------------------------------------------------------------------------
  const listQuery = useQuery({
    queryKey: ['payout-attachments', payoutId],
    queryFn: async (): Promise<PayoutAttachment[]> => {
      if (!payoutId) return [];
      const { data, error } = await (supabase.rpc as any)(
        'list_payout_attachments',
        {
          p_payout_id: payoutId,
          p_tenant_hint: tenantId,
        },
      );
      if (error) {
        log.error('Falha ao listar anexos', error);
        throw error;
      }
      return (data ?? []) as PayoutAttachment[];
    },
    enabled: !!payoutId,
    staleTime: 30 * 1000,
  });

  // ---------------------------------------------------------------------------
  // MUTATION: upload (storage + RPC add)  — admin
  // ---------------------------------------------------------------------------
  const uploadMutation = useMutation({
    mutationFn: async (file: File): Promise<PayoutAttachment> => {
      if (!payoutId) throw new Error('payoutId é obrigatório');
      if (!tenantId) throw new Error('Tenant ativo não definido');

      // Validações client-side (a RPC valida novamente no servidor)
      if (file.size > MAX_BYTES) {
        throw new Error(`Arquivo "${file.name}" excede 10 MB`);
      }
      if (!ALLOWED_MIME.has(file.type)) {
        throw new Error(
          `Tipo "${file.type || 'desconhecido'}" não permitido. Aceitos: imagens e PDF.`,
        );
      }

      const safeName = sanitizeFileName(file.name);
      const uniqueId = crypto.randomUUID();
      const storagePath = `financeiro/${tenantId}/${payoutId}/${uniqueId}-${safeName}`;

      // 1) Upload no bucket
      const { error: upErr } = await supabase.storage
        .from(BUCKET)
        .upload(storagePath, file, {
          contentType: file.type,
          upsert: false,
        });
      if (upErr) {
        log.error('Falha no upload do storage', upErr);
        throw upErr;
      }

      // 2) Persiste registro
      const { data, error } = await (supabase.rpc as any)(
        'add_payout_attachment',
        {
          p_payout_id: payoutId,
          p_storage_path: storagePath,
          p_file_name: safeName,
          p_file_size: file.size,
          p_mime_type: file.type,
          p_tenant_hint: tenantId,
        },
      );

      if (error) {
        // rollback: remove arquivo do storage para não deixar lixo
        await supabase.storage.from(BUCKET).remove([storagePath]);
        log.error('Falha ao registrar anexo', error);
        throw error;
      }
      return data as PayoutAttachment;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payout-attachments', payoutId] });
      qc.invalidateQueries({ queryKey: ['referral-payouts-paginated'] });
    },
  });

  // ---------------------------------------------------------------------------
  // MUTATION: remoção (RPC delete + storage.remove)  — admin
  // ---------------------------------------------------------------------------
  const removeMutation = useMutation({
    mutationFn: async (attachmentId: string): Promise<void> => {
      const { data, error } = await (supabase.rpc as any)(
        'delete_payout_attachment',
        {
          p_attachment_id: attachmentId,
          p_tenant_hint: tenantId,
        },
      );
      if (error) {
        log.error('Falha ao remover anexo', error);
        throw error;
      }
      const storagePath = data as string | null;
      if (storagePath) {
        await supabase.storage.from(BUCKET).remove([storagePath]);
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payout-attachments', payoutId] });
      qc.invalidateQueries({ queryKey: ['referral-payouts-paginated'] });
    },
  });

  return {
    attachments: listQuery.data ?? [],
    isLoading: listQuery.isLoading,
    isFetching: listQuery.isFetching,
    error: listQuery.error as Error | null,
    refetch: listQuery.refetch,
    upload: uploadMutation.mutateAsync,
    isUploading: uploadMutation.isPending,
    remove: removeMutation.mutateAsync,
    isRemoving: removeMutation.isPending,
  };
}
